import { gql } from '@apollo/client';
import React, { FunctionComponent } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import PATHS from '../../constants/paths';
import { PageStatus, TeamsListQuery } from '../../../types/__generated__';
import { SIZE_136_136 } from '../../constants/imageTemplateStrings';

import BenefactorListItem from '../BenefactorListItem/BenefactorListItem';
import PageSection from '../PageSection/PageSection';
import LoadMoreButton from '../LoadMoreButton/LoadMoreButton';
import BenefactorListItemSkeleton from '../BenefactorListItem/BenefactorListItemSkeleton';
import useFullPageQuery from '../../hooks/useFullPageQuery';

import styles from './TeamsList.scss';

const DOCUMENT = gql`
  query TeamsList($after: String, $type: PageType!, $slug: Slug!) {
    page(type: $type, slug: $slug) {
      id
      relationships {
        teams(first: 5, after: $after) {
          edges {
            node {
              id
              slug
              title
              status
              owner {
                id
                avatar
              }
              donationSummary {
                totalAmount {
                  currencyCode
                  value
                }
                totalMatched {
                  type
                  amount {
                    currencyCode
                    value
                  }
                }
              }
              targetWithCurrency {
                value
              }
              donations(first: 1) {
                totalCount
              }
            }
          }
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
`;

const TeamsList: FunctionComponent = () => {
  const { t } = useTranslation('common', { useSuspense: false });
  const { loading, error, data, fetchMore } = useFullPageQuery<TeamsListQuery>(
    DOCUMENT,
    {
      notifyOnNetworkStatusChange: true,
    },
  );

  if (error) {
    return (
      <PageSection title={t('page.teamsListTitle', 'Teams')} hasBorder>
        <BenefactorListItemSkeleton animated={false} />
      </PageSection>
    );
  }

  if (
    loading ||
    !data ||
    !data.page?.relationships?.teams ||
    data.page.relationships.teams.totalCount === 0
  ) {
    return null;
  }

  const {
    teams: { edges, totalCount },
  } = data.page.relationships;

  async function handleFetchMore() {
    await fetchMore({
      variables: {
        after: data?.page?.relationships?.teams.pageInfo.endCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (
          !previousResult?.page?.relationships?.teams ||
          !fetchMoreResult?.page?.relationships?.teams
        ) {
          return previousResult;
        }

        return {
          page: {
            ...previousResult.page,
            relationships: {
              ...fetchMoreResult.page.relationships,
              teams: {
                ...fetchMoreResult.page.relationships.teams,
                edges: [
                  ...previousResult.page.relationships.teams.edges,
                  ...fetchMoreResult.page.relationships.teams.edges,
                ],
              },
            },
          },
        };
      },
    });
  }

  return (
    <PageSection
      title="Teams"
      hasBorder
      count={totalCount}
      className={styles.container}
    >
      <ul data-qa="teams-list">
        {edges.map(team => {
          const { donationSummary, ...teamPage } = team.node;

          let totaliserValue;
          if (
            teamPage.targetWithCurrency &&
            teamPage.targetWithCurrency.value > 0
          ) {
            totaliserValue = Math.round(
              (donationSummary?.totalAmount?.value /
                teamPage.targetWithCurrency.value) *
                100,
            );
          }

          return (
            <BenefactorListItem
              key={teamPage.id}
              title={teamPage.title}
              image={
                teamPage.owner?.avatar
                  ? `${teamPage.owner?.avatar}?template=${SIZE_136_136}`
                  : ''
              }
              currencyCode={donationSummary?.totalAmount?.currencyCode || 'GBP'}
              amount={
                donationSummary?.totalAmount?.value
                  ? donationSummary.totalAmount.value / 100
                  : 0
              }
              totaliserValue={totaliserValue ?? null}
              supporterCount={teamPage.donations?.totalCount || 0}
              url={teamPage.slug ? `${PATHS.TEAMS}${teamPage.slug}` : undefined}
              pageStatus={teamPage.status ?? PageStatus.Active}
            />
          );
        })}
      </ul>
      {data.page.relationships.teams.pageInfo.hasNextPage && (
        <LoadMoreButton
          className="jg-btn"
          loading={loading}
          onClick={handleFetchMore}
          trackingData={{
            action_name: 'click',
            subtype: 'button',
            event: 'click',
            event_value: 'show more',
            sub_section: 'campaign members',
          }}
        >
          <Trans i18nKey="members.showMore">
            Show more
            <span className="jg-sr-only"> members</span>
          </Trans>
        </LoadMoreButton>
      )}
    </PageSection>
  );
};

export default TeamsList;
